/**
 * Shortcuts page. Figma `9229:190120` (content `9229:190276`).
 *
 * Section titles `9229:190285`: 14/22 medium, Color/text/primary, px 8, pb 14,
 * no top padding (bare). The 24px gap between blocks is `SettingsPane`. Rows
 * `9229:190291`: 54px, px 20, py 16. The name and the chord are both
 * Color/text/primary, 14/22. The card is the shared 14px / gray100-80% card.
 *
 * The frame's hairline is inset 20px. This page uses the window's full-bleed
 * `SettingsHairline` so the card matches every other page in the same window.
 *
 * Rows are the resolved keybinding table (`useKeybindings`), not a second
 * catalog. The chord spelling is `formatChord` (`Ctrl+Shift+P`), the same
 * string search hints use.
 */
import { PLATFORM_MODULE_ID } from '@hibro/shared';
import {
  SETTINGS_CARD,
  SETTINGS_ROW,
  SettingsHairline,
  SettingsHint,
  SettingsPane,
} from '@hibro/ui-client';
import { type ReactNode, useMemo } from 'react';

import { resolveCommandLabel } from '../../commands/command-query';
import { useTranslator } from '../../i18n/i18n';
import { formatChord } from '../../keybindings/chord';
import { useKeybindings } from '../../keybindings/keybinding-host';
import { groupShortcutRows, type ShortcutSection } from './shortcuts';

export function ShortcutsPage(): ReactNode {
  const { t, title } = useTranslator();
  const { bindings, commands, layout, mac } = useKeybindings();
  const sections = useMemo(() => {
    const rows = bindings.map((entry) => {
      const command = commands.find(
        (candidate) => candidate.id === entry.binding.command,
      );
      const category = command?.category
        ? title(command.moduleId, command.category)
        : undefined;
      return {
        id: entry.binding.id,
        moduleId: entry.binding.moduleId || PLATFORM_MODULE_ID,
        label: resolveCommandLabel(entry.binding.command, commands, title),
        chord: entry.chord
          ? formatChord(entry.chord, { mac, ...(layout ? { layout } : {}) })
          : entry.binding.key,
        ...(category ? { category } : {}),
      };
    });
    return groupShortcutRows(rows, {
      shell: t('settingsWindow.shortcut.shell'),
    });
  }, [bindings, commands, layout, mac, t, title]);

  return (
    <SettingsPane>
      {sections.length === 0 ? (
        <SettingsHint>{t('settingsWindow.shortcut.empty')}</SettingsHint>
      ) : (
        sections.map((section) => (
          <ShortcutCard key={section.moduleId} section={section} />
        ))
      )}
    </SettingsPane>
  );
}

function ShortcutCard({ section }: { section: ShortcutSection }): ReactNode {
  return (
    <div className="flex w-full flex-col">
      <p className="w-full px-2 pb-3.5 text-[14px] leading-[22px] font-medium text-(--app-color-text-primary)">
        {section.title}
      </p>
      <ul className={`${SETTINGS_CARD} m-0 list-none p-0`}>
        {section.rows.map((row, index) => (
          <li key={row.id} className="flex w-full flex-col">
            <div className={`${SETTINGS_ROW} gap-4 py-4`}>
              <span className="min-w-0 flex-1 truncate text-[14px] leading-[22px] text-(--app-color-text-primary)">
                {row.label}
              </span>
              <span className="shrink-0 text-[14px] leading-[22px] text-(--app-color-text-primary)">
                {row.chord}
              </span>
            </div>
            {index === section.rows.length - 1 ? null : <SettingsHairline />}
          </li>
        ))}
      </ul>
    </div>
  );
}
